/**
 * Os contadores das abas, portados de app.js:46-53 e knowledge.js:38-54.
 *
 * O ponto que não pode se perder: falha não é zero. No painel atual, cada fonte
 * do conhecimento falha por conta própria e a que caiu vira "!" no badge, com o
 * motivo no title — porque um zero silencioso faz o painel afirmar que o time
 * não tem decisão nenhuma quando, na verdade, ele não conseguiu ler.
 */
import { useMemo } from 'react';
import { useContagemDeNotas, useDecisoes, usePropostas, useProjetos, useSaude, useSessoes } from '../api/consultas';

export interface Contador {
  estado: 'carregando' | 'ok' | 'erro';
  valor?: string | number;
  titulo?: string;
  /** amarelo: tem coisa esperando gente */
  alerta?: boolean;
}

const erro = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export function useContadores(slug: string): Record<string, Contador> {
  const projetos = useProjetos();
  const notas = useContagemDeNotas(slug);
  const decisoes = useDecisoes(slug);
  const propostas = usePropostas(slug);
  const sessoes = useSessoes(slug);
  const saude = useSaude(slug);

  const projeto = projetos.data?.find((p) => p.slug === slug);

  return useMemo(() => {
    const de = <T,>(
      c: { data?: T; isPending: boolean; error: unknown },
      pronto: (d: T) => Contador,
    ): Contador => {
      if (c.error) return { estado: 'erro', titulo: erro(c.error) };
      if (c.isPending || c.data === undefined) return { estado: 'carregando' };
      return pronto(c.data);
    };

    const total = (m: Record<string, number>) => Object.values(m).reduce((a, b) => a + b, 0);
    const naoConfirmadas = sessoes.data?.unconfirmed?.length || 0;

    return {
      graph: projetos.error
        ? { estado: 'erro', titulo: erro(projetos.error) }
        : projeto
          ? { estado: 'ok', valor: projeto.nodes || 0 }
          : { estado: 'carregando' },
      notes: de(notas, (c) => ({ estado: 'ok', valor: total(c) })),
      // a fila de revisão é uma só para quem revisa: proposta da IA mais resumo
      // de sessão esperando confirmação
      review: propostas.error || sessoes.error
        ? { estado: 'erro', titulo: erro(propostas.error || sessoes.error) }
        : propostas.isPending || sessoes.isPending
          ? { estado: 'carregando' }
          : {
            estado: 'ok',
            valor: (propostas.data?.pending || 0) + naoConfirmadas,
            alerta: (propostas.data?.pending || 0) + naoConfirmadas > 0,
          },
      decisions: de(decisoes, (d) => ({ estado: 'ok', valor: d.length })),
      sessions: de(sessoes, (s) => {
        const abertas = (s.sessions || []).filter((x) => x.status === 'open').length;
        return {
          estado: 'ok',
          valor: (s.sessions || []).length,
          titulo: abertas ? `${abertas} sessão(ões) aberta(s)` : 'sessões de trabalho',
          alerta: (s.unconfirmed || []).length > 0,
        };
      }),
      health: de(saude, (h) => ({
        estado: 'ok',
        valor: `${h.coverage_pct}%`,
        titulo: 'cobertura de documentação',
      })),
    };
  }, [projetos.error, projeto, notas, decisoes, propostas, sessoes, saude]);
}
